/*
Pestaña "Destacados" del formulario de ChileAutos: saldo en vivo de los productos
contratados (Premium, TopSpot, Showcase, TopNitro), los que tiene este aviso y
aplicar/quitar.

Las reglas las impone la API (chAApi/utils/vehicleTags.ts); acá sólo se evita
ofrecer lo que igual va a rechazar. Lo que las explica, probado en staging:
ChileAutos no deja agregar un producto junto a otro que el aviso ya tiene sin
quitarlo y volverlo a aplicar. Con Premium eso es gratis, pero un TopSpot,
Showcase o TopNitro se volvería a cobrar. Por eso, mientras uno está activo,
no se ofrece aplicar nada más ni quitar sólo el Premium.

Los destacados aplicados desde el portal de ChileAutos no se ven acá: la API de
ChileAutos no permite leer los de un aviso.
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@radix-ui/themes'

import useChaProducts from '../hooks/useChaProducts'
import { useAuth } from '../hooks/useAuth'
import { UserProfileRole } from '../modules/users/types/UserProfile'
import { chaDaysLeft, chaDaysLeftLabel, isChaConsumable, isChaProductActive } from '../utils/chaProducts'

import ConfirmationModal from './ConfirmationModal'
import { Info } from './MessageInfo'

import type { ChaAppliedProduct, ChaProduct, ChaProductModality, ChaRequestedTag } from '../interfaces/ChaProducts'
import type Vehicle from '../interfaces/Vehicle'

interface ChileAutosProductsProps {
  vehicle: Vehicle
  // Recarga el vehículo sin cerrar el formulario.
  onChange: () => void
}

const modalityLabel = (modality: ChaProductModality): string =>
  modality === 'oneshot' ? 'uso único' : modality === 'recurrente' ? 'mensual' : 'reutilizable'

// `top_nitro_5/7_5` → "5 leads / 7 días"
const variantLabel = (variant?: string | null): string => {
  const match = /^top_nitro_(\d+)\/(\d+)_/i.exec(variant ?? '')

  return match ? `${match[1]} leads / ${match[2]} días` : (variant ?? '')
}

const productLabel = (product: { name: string; variant?: string | null }): string =>
  product.variant ? `${product.name} (${variantLabel(product.variant)})` : product.name

const toTag = (product: { code: string; variant?: string | null; modality: ChaProductModality }): ChaRequestedTag => ({
  code: product.code,
  variant: product.variant ?? null,
  modality: product.modality,
})

const sameProduct = (a: { code: string }, b: { code: string }) => a.code.toLowerCase() === b.code.toLowerCase()

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'long', timeZone: 'America/Santiago' })

interface PendingChange {
  tags: ChaRequestedTag[]
  message: string
  consumable: boolean
}

const ChileAutosProducts: React.FC<ChileAutosProductsProps> = ({ vehicle, onChange }) => {
  const { userData, hasRole } = useAuth()
  const { chaProductsGet, chaVehicleTags, loadingProducts, applying } = useChaProducts()

  const canManage = hasRole(UserProfileRole.ManageExternalProducts)
  const organizationId = userData?.organization?._id ?? ''
  const isPublished = !!vehicle.external_chileautos?.identifier

  const [balance, setBalance] = useState<ChaProduct[] | null>(null)
  const [balanceError, setBalanceError] = useState<string>('')
  // Lo que devolvió la API tras aplicar, hasta que llegue el vehículo recargado.
  const [localApplied, setLocalApplied] = useState<ChaAppliedProduct[] | null>(null)
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null)
  const [message, setMessage] = useState<string>('')
  const [messageTrigger, setMessageTrigger] = useState<number>(0)

  const vehicleProducts = vehicle.external_chileautos?.products

  useEffect(() => {
    setLocalApplied(null)
  }, [vehicleProducts])

  const loadBalance = useCallback(async () => {
    if (!organizationId) return

    setBalanceError('')

    try {
      setBalance(await chaProductsGet(organizationId))
    } catch (err) {
      setBalanceError(err instanceof Error ? err.message : 'No se pudo consultar el saldo.')
    }
  }, [organizationId, chaProductsGet])

  useEffect(() => {
    void loadBalance()
  }, [loadBalance])

  const applied = localApplied ?? vehicleProducts ?? []
  const now = new Date()
  const active = applied.filter((product) => isChaProductActive(product, now))
  const expired = applied.filter((product) => !isChaProductActive(product, now))
  const activeConsumable = active.find((product) => isChaConsumable(product.code))

  const available = useMemo(() => (balance ?? []).filter((product) => product.supported), [balance])
  const unsupported = useMemo(() => (balance ?? []).filter((product) => !product.supported), [balance])

  const notify = (text: string) => {
    setMessage(text)
    setMessageTrigger(Date.now())
  }

  const send = async (tags: ChaRequestedTag[], confirmConsumable: boolean) => {
    setPendingChange(null)

    try {
      const result = await chaVehicleTags(vehicle._id, tags, confirmConsumable)
      setLocalApplied(result.products)
      notify(result.pending ? `⏳ ${result.message}` : `✅ ${result.message}`)
    } catch (err) {
      notify(`❌ ${err instanceof Error ? err.message : 'No se pudo cambiar los destacados.'}`)
    } finally {
      // Tras un error el aviso también puede haber cambiado (p. ej. quedó vacío): se recarga igual.
      await loadBalance()
      onChange()
    }
  }

  const askApply = (product: ChaProduct) => {
    const tags = [...active.map(toTag), toTag(product)]

    if (!product.consumable) {
      void send(tags, false)

      return
    }

    const duration = product.durationDays ? `Dura ${product.durationDays} días` : 'Se consume al aplicarlo'
    setPendingChange({
      tags,
      consumable: true,
      message:
        `Esto usa 1 de tus ${product.available} ${productLabel(product)}. ${duration} y no se reembolsa ` +
        'si vendes o despublicas el vehículo. ¿Continuar?',
    })
  }

  const askRemove = (toRemove: ChaAppliedProduct[]) => {
    const tags = active.filter((product) => !toRemove.includes(product)).map(toTag)
    const lost = toRemove.filter((product) => isChaConsumable(product.code))

    if (lost.length === 0) {
      void send(tags, false)

      return
    }

    const detail = lost
      .map((product) => `${productLabel(product)} (${chaDaysLeftLabel(chaDaysLeft(product, now))})`)
      .join(', ')
    setPendingChange({
      tags,
      consumable: true,
      message: `Quitar ${detail} pierde los días que le quedan y el cupo no se reembolsa. ¿Continuar?`,
    })
  }

  // Por qué no se puede aplicar este producto ahora, o `null` si se puede.
  const applyBlockedReason = (product: ChaProduct): string | null => {
    if (active.some((current) => sameProduct(current, product))) return 'Ya aplicado'

    if (product.available <= 0) return 'Sin cupos'

    if (activeConsumable) return `Disponible cuando venza el ${activeConsumable.name}`

    return null
  }

  if (!isPublished) {
    return (
      <div className="py-8 text-center text-gray-500">Publica el vehículo en ChileAutos para poder destacarlo.</div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Destacados en ChileAutos</h3>
        <Button variant="soft" onClick={loadBalance} disabled={loadingProducts || applying}>
          {loadingProducts ? 'Consultando...' : 'Actualizar saldo'}
        </Button>
      </div>

      {/* En este aviso */}
      <section className="space-y-2">
        <h4 className="font-medium text-gray-700">En este aviso</h4>
        {active.length === 0 && expired.length === 0 && (
          <p className="text-sm text-gray-500">Este aviso no tiene destacados aplicados desde Veekls.</p>
        )}
        {active.map((product) => {
          // Quitar sólo el Premium con un consumible activo obligaría a volver a cobrar el consumible.
          const removable = canManage && (isChaConsumable(product.code) || !activeConsumable)

          return (
            <div
              key={`${product.code}-${product.appliedAt}`}
              className="flex items-center justify-between rounded-md border border-orange-200 bg-orange-50 px-3 py-2"
            >
              <div className="text-sm">
                <span className="font-semibold">{productLabel(product)}</span>
                <span className="text-gray-600"> · {modalityLabel(product.modality)}</span>
                <span className="text-gray-600"> · {chaDaysLeftLabel(chaDaysLeft(product, now))}</span>
                {product.status === 'pending' && (
                  <span
                    className="ml-2 text-yellow-700"
                    title="ChileAutos recibió el cambio pero el saldo todavía no lo refleja."
                  >
                    (ChileAutos aún no lo confirma)
                  </span>
                )}
              </div>
              {removable && (
                <Button size="1" variant="outline" color="red" onClick={() => askRemove([product])} disabled={applying}>
                  Quitar
                </Button>
              )}
            </div>
          )
        })}
        {expired.map((product) => (
          <div
            key={`${product.code}-${product.appliedAt}`}
            className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-400"
          >
            <span className="font-semibold">{productLabel(product)}</span>
            <span> · venció el {product.expiresAt ? formatDate(product.expiresAt) : ''}</span>
          </div>
        ))}
        {canManage && active.length > 1 && (
          <Button size="1" variant="ghost" color="red" onClick={() => askRemove(active)} disabled={applying}>
            Quitar todos
          </Button>
        )}
      </section>

      {/* Saldo */}
      <section className="space-y-2">
        <h4 className="font-medium text-gray-700">Tus productos contratados</h4>
        {balanceError && <p className="text-sm text-red-600">{balanceError}</p>}
        {!balance && !balanceError && <p className="text-sm text-gray-500">Consultando saldo en ChileAutos...</p>}
        {balance && balance.length === 0 && (
          <p className="text-sm text-gray-500">No tienes productos contratados en ChileAutos.</p>
        )}
        {available.map((product) => {
          const blocked = applyBlockedReason(product)

          return (
            <div
              key={`${product.code}-${product.variant ?? ''}-${product.modality ?? ''}`}
              className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2"
            >
              <div className="text-sm">
                <span className="font-semibold">{productLabel(product)}</span>
                <span className="text-gray-600"> · {modalityLabel(product.modality)}</span>
                <span className={product.available > 0 ? 'text-gray-600' : 'text-red-600'}>
                  {' '}
                  · {product.available} de {product.quantity} disponibles
                </span>
              </div>
              {canManage &&
                (blocked ? (
                  <span className="text-xs text-gray-500">{blocked}</span>
                ) : (
                  <Button size="1" onClick={() => askApply(product)} disabled={applying}>
                    Aplicar
                  </Button>
                ))}
            </div>
          )
        })}
        {unsupported.map((product) => (
          <div
            key={`${product.code}-${product.variant ?? ''}-${product.modality ?? ''}`}
            className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2 text-sm text-gray-400"
          >
            <span>
              {productLabel(product)} · {product.available} de {product.quantity} disponibles
            </span>
            <span className="text-xs">No disponible desde Veekls</span>
          </div>
        ))}
      </section>

      {applying && (
        <p className="text-center text-sm font-bold text-blue-600">
          Aplicando en ChileAutos... puede tardar hasta 30 segundos.
        </p>
      )}

      {message && (
        <div className="flex justify-center">
          <Info message={message} trigger={messageTrigger} onTimeout={() => setMessage('')} noCenter={true} />
        </div>
      )}

      <div className="space-y-1 rounded-md bg-gray-50 p-3 text-xs text-gray-600">
        {!canManage && <p>No tienes permiso para aplicar destacados. Pídeselo a un administrador.</p>}
        <p>
          TopSpot y Showcase duran 7 días desde que se aplican; TopNitro, los días de su plan. El cupo se consume al
          aplicarlo y no se reembolsa aunque vendas o despubliques el vehículo. Premium se libera al quitarlo.
        </p>
        <p>
          Los destacados aplicados desde el portal de ChileAutos no se ven aquí. Si este aviso tiene uno aplicado allá,
          aplicar otro desde Veekls lo reemplaza.
        </p>
      </div>

      {pendingChange && (
        <ConfirmationModal
          message={pendingChange.message}
          onConfirm={() => send(pendingChange.tags, pendingChange.consumable)}
          onCancel={() => setPendingChange(null)}
        />
      )}
    </div>
  )
}

export default ChileAutosProducts
